import React from 'react';
import { motion } from 'framer-motion';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const UserPermissions = () => {
  const permissionFeatures = [
  {
    icon: "Shield",
    title: "Role-Based Access Control",
    description: "Define custom roles with specific permissions for managers, cashiers, and inventory staff to ensure secure operations."
  },
  {
    icon: "Users",
    title: "Multi-User Management",
    description: "Add unlimited team members with individual login credentials and track their activities across all locations."
  },
  {
    icon: "Eye",
    title: "Activity Monitoring",
    description: "Monitor user actions with detailed audit logs showing who made changes, when, and what was modified."
  },
  {
    icon: "Lock",
    title: "Sensitive Data Protection",
    description: "Restrict access to financial reports, pricing changes, and customer data based on user roles and responsibilities."
  }];


  return (
    <section className="py-16 md:py-20 lg:py-24 bg-background">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}>

            <div className="inline-flex items-center px-4 py-2 rounded-full bg-accent/10 mb-4 md:mb-6">
              <Icon name="Shield" size={18} color="var(--color-accent)" strokeWidth={2} />
              <span className="ml-2 font-body text-sm font-medium text-accent">User Permissions</span>
            </div>
            
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-4 md:mb-6">
              Secure Multi-User Access Management
            </h2>
            
            <p className="font-body text-base md:text-lg text-muted-foreground mb-6 md:mb-8">
              Empower your team while maintaining security with granular permission controls and comprehensive activity tracking across all user actions.
            </p>
            
            <div className="space-y-4 md:space-y-6">
              {permissionFeatures?.map((feature, index) =>
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex items-start space-x-4">

                  <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-lg bg-accent/10 flex items-center justify-center">
                    <Icon name={feature?.icon} size={20} color="var(--color-accent)" strokeWidth={2} />
                  </div>
                  <div>
                    <h3 className="font-heading text-base md:text-lg font-semibold text-foreground mb-1 md:mb-2">
                      {feature?.title}
                    </h3>
                    <p className="font-body text-sm md:text-base text-muted-foreground">
                      {feature?.description}
                    </p>
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative">

            <div className="relative rounded-2xl overflow-hidden shadow-purple-lg">
              <Image
                src="/cloud-app-screenshot/settings-light.png"
                alt="Cultaz settings page showing user management and permission controls"
                className="w-full h-64 md:h-80 lg:h-96 object-cover" />

              <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent"></div>
            </div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="absolute -bottom-4 -right-4 md:-bottom-6 md:-right-6 bg-card rounded-xl shadow-purple-md p-4 md:p-6 border border-border">

              <div className="flex items-center space-x-3 md:space-x-4">
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-lg bg-accent/10 flex items-center justify-center">
                  <Icon name="Users" size={24} color="var(--color-accent)" strokeWidth={2} />
                </div>
                <div>
                  <p className="font-body text-xs md:text-sm text-muted-foreground">Active Users</p>
                  <p className="font-heading text-xl md:text-2xl font-bold text-foreground">2,500+</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>);


};

export default UserPermissions;